import { FC, lazy, Suspense } from 'react'

import { useMountedDialogs } from '../../hooks/useMountedDialogs'
import { useUAInsightsParams } from '../../hooks/useUAInsightsNavigation'
import { UAInsightsDialogType } from '../../types'
import { AlignmentDetailsDialog } from '../AlignmentDetailsDialog/AlignmentDetailsDialog'
import { AllCreativesDialog } from '../AllCreativesDialog/AllCreativesDialog'
import { AllGamesDialog } from '../AllGamesDialog/AllGamesDialog'
import { AllTagsDialog } from '../AllTagsDialog/AllTagsDialog'
import { BlindSpotDetailsDialog } from '../BlindSpotDetailsDialog/BlindSpotDetailsDialog'
import { CompareGameDialog } from '../CompareGameDialog/CompareGameDialog'
import { CreativeAdDetailsDialog } from '../CreativeAdDetailsDialog/CreativeAdDetailsDialog'
import { CreativeTagDetailsDialog } from '../CreativeTagDetailsDialog/CreativeTagDetailsDialog'
import { DimensionTagsListingDialog } from '../DimensionTagsListingDialog/DimensionTagsListingDialog'
import { GenreTagsListingDialog } from '../GenreTagsListingDialog/GenreTagsListingDialog'
import { MotivationDetailsDialog } from '../MotivationDetailsDialog/MotivationDetailsDialog'
import { TagGamesDialog } from '../TagGamesDialog/TagGamesDialog'

// Lazy-loaded drilldowns: splitting the heavyweight Shared/Diverging motivation
// dialogs (Chart.js imports + comparison UI) out keeps them off the initial UA
// Insights chunk for users who only browse a single game.
const SharedMotivationDetailsDialog = lazy(() =>
  import('../SharedMotivationDetailsDialog/SharedMotivationDetailsDialog').then((m) => ({
    default: m.SharedMotivationDetailsDialog,
  }))
)
const DivergingMotivationDetailsDialog = lazy(() =>
  import('../DivergingMotivationDetailsDialog/DivergingMotivationDetailsDialog').then((m) => ({
    default: m.DivergingMotivationDetailsDialog,
  }))
)

/**
 * Every drilldown dialog, keyed by the `dialog` URL param that opens it.
 * Typing this as a total `Record` makes a new member of
 * `UA_INSIGHTS_DIALOG_TYPES` a compile error until it is wired up here.
 */
const DIALOG_COMPONENTS: Record<UAInsightsDialogType, FC> = {
  tag: CreativeTagDetailsDialog,
  ad: CreativeAdDetailsDialog,
  motivation: MotivationDetailsDialog,
  'blind-spot': BlindSpotDetailsDialog,
  alignment: AlignmentDetailsDialog,
  'shared-motivation': SharedMotivationDetailsDialog,
  'diverging-motivation': DivergingMotivationDetailsDialog,
  'genre-tags': GenreTagsListingDialog,
  'tag-games': TagGamesDialog,
  'dimension-tags': DimensionTagsListingDialog,
  'all-creatives': AllCreativesDialog,
  'all-games': AllGamesDialog,
  'all-tags': AllTagsDialog,
  'compare-game': CompareGameDialog,
}

/**
 * Mounts the active URL-gated drilldown dialog for the UA Insights game page
 * and Compare page. Comparison *results* are no longer dialogs — they are the
 * routed Compare page (`UAInsightsComparePage`).
 */
export const UAInsightsDialogs: FC = () => {
  const { params } = useUAInsightsParams()
  const mounted = useMountedDialogs(params.dialog)

  return (
    <Suspense fallback={null}>
      {mounted.map((key) => {
        const Dialog = DIALOG_COMPONENTS[key]
        return <Dialog key={key} />
      })}
    </Suspense>
  )
}
